// ─── batch-41 dossier (FO-355 + FO-358) — dossier-volledigheid + informatie-opvragen ───
//
// Gedeelde constanten, Zod-schemas en DTO's voor de dossier-volledigheidscheck
// (FO-355) en het handmatig informatie-opvragen (FO-358). De GEGEVEN-sleutels
// zijn een VASTE, code-gedefinieerde set: elk heeft een eigen aanwezigheids-
// predicaat in `apps/api/src/lib/dossier-volledigheid.ts` dat getypeerd is op
// de Prisma-Employee-velden, zodat een veld-hernoeming de typecheck breekt
// (spec 57 §13.1 — geen dynamische veld-reflectie).
//
// Privacy (spec 57 §3.3 / 60 §6): overal in dit domein reizen alleen SLEUTELS
// en LABELS — nooit (deel)waarden van wat er wél of niet staat.

import { z } from 'zod'
import { DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS, type DocumentTypeValue } from './employee-documents'
import type { MutatieType } from './selfservice'

// ─── Soorten + sleutels ──────────────────────────────────────────────────────

/** Mirrort Prisma-enum `DossierVereisteSoort`. */
export const DOSSIER_VEREISTE_SOORTEN = ['DOCUMENT', 'GEGEVEN'] as const
export type DossierVereisteSoortValue = (typeof DOSSIER_VEREISTE_SOORTEN)[number]

/**
 * De code-vaste set checkbare GEGEVEN-sleutels (spec 57 §3.2). Uitbreiden =
 * bewust een nieuw predicaat schrijven in de API-lib; de Zod-enum hieronder
 * weigert onbekende sleutels.
 */
export const DOSSIER_GEGEVEN_KEYS = [
  'BSN',
  'IBAN',
  'ADRES',
  'GEBOORTEDATUM',
  'NOODCONTACT',
  'PRIVE_TELEFOON',
  'PRIVE_EMAIL',
  'RIJBEWIJS_VERVALDATUM',
] as const
export type DossierGegevenKey = (typeof DOSSIER_GEGEVEN_KEYS)[number]

/** NL-labels zoals ze in meldingen/UI verschijnen (alleen labels, nooit waarden). */
export const DOSSIER_GEGEVEN_LABELS: Record<DossierGegevenKey, string> = {
  BSN: 'BSN',
  IBAN: 'IBAN',
  ADRES: 'adres',
  GEBOORTEDATUM: 'geboortedatum',
  NOODCONTACT: 'noodcontact',
  PRIVE_TELEFOON: 'privé-telefoonnummer',
  PRIVE_EMAIL: 'privé-e-mailadres',
  RIJBEWIJS_VERVALDATUM: 'rijbewijs-vervaldatum',
}

/**
 * Welke selfservice-mutatie (MutatieVerzoek.type) een GEGEVEN aanlevert.
 * Gebruikt voor (a) de onderdrukking "aanlevering onderweg" (spec 57 §3.2) en
 * (b) de vraag of een medewerker het item überhaupt zelf kan aanleveren.
 * Sleutels zonder mapping (BSN, geboortedatum, rijbewijs-vervaldatum) hebben
 * geen selfservice-kanaal — die lopen via HR.
 */
export const DOSSIER_GEGEVEN_MUTATIE_TYPE: Partial<Record<DossierGegevenKey, MutatieType>> = {
  IBAN: 'BANK',
  ADRES: 'ADRES',
  NOODCONTACT: 'NOODCONTACT',
  PRIVE_TELEFOON: 'PRIVE_TELEFOON',
  PRIVE_EMAIL: 'PRIVE_EMAIL',
}

/** Mirrort Prisma-enum `InformatieVerzoekStatus`. */
export const INFORMATIE_VERZOEK_STATUSSEN = ['OPEN', 'AFGEROND', 'VERVALLEN', 'GEANNULEERD'] as const
export type InformatieVerzoekStatusValue = (typeof INFORMATIE_VERZOEK_STATUSSEN)[number]

export const INFORMATIE_VERZOEK_STATUS_LABELS: Record<InformatieVerzoekStatusValue, string> = {
  OPEN: 'Open',
  AFGEROND: 'Afgerond',
  VERVALLEN: 'Vervallen',
  GEANNULEERD: 'Geannuleerd',
}

// ─── Deeplinks naar /mijn-gegevens (FO-358 §14.4 — gedeelde constante) ───────

/** Subset van de portal-mijn-gegevens-tab-keys die dossier-items aanwijzen. */
export type DossierMijnGegevensTab = 'persoonlijk' | 'bank' | 'noodcontact' | 'documenten'

/**
 * GEGEVEN-sleutel → mijn-gegevens-tab. `null` = geen selfservice-tab (het item
 * is niet zelf aanleverbaar; melding/deeplink alleen richting HR).
 */
export const DOSSIER_GEGEVEN_MIJN_GEGEVENS_TAB: Record<DossierGegevenKey, DossierMijnGegevensTab | null> = {
  BSN: null,
  IBAN: 'bank',
  ADRES: 'persoonlijk',
  GEBOORTEDATUM: null,
  NOODCONTACT: 'noodcontact',
  PRIVE_TELEFOON: 'persoonlijk',
  PRIVE_EMAIL: 'persoonlijk',
  RIJBEWIJS_VERVALDATUM: null,
}

/** Tab voor een dossier-item (DOCUMENT-items landen altijd op het documenten-tab). */
export function dossierItemMijnGegevensTab(
  soort: DossierVereisteSoortValue,
  sleutel: string,
): DossierMijnGegevensTab | null {
  if (soort === 'DOCUMENT') return 'documenten'
  return DOSSIER_GEGEVEN_MIJN_GEGEVENS_TAB[sleutel as DossierGegevenKey] ?? null
}

/** Stabiele item-key "SOORT:SLEUTEL" (stand-administratie + idempotency). */
export function dossierSleutelKey(soort: DossierVereisteSoortValue, sleutel: string): string {
  return `${soort}:${sleutel}`
}

/** Default-label voor een sleutel (seed/fallback; HR kan het label overschrijven). */
export function dossierSleutelDefaultLabel(
  soort: DossierVereisteSoortValue,
  sleutel: string,
): string {
  if (soort === 'DOCUMENT') {
    return DOCUMENT_TYPE_LABELS[sleutel as DocumentTypeValue] ?? sleutel
  }
  return DOSSIER_GEGEVEN_LABELS[sleutel as DossierGegevenKey] ?? sleutel
}

// ─── Functietypes (scope) ────────────────────────────────────────────────────

/** Mirrort Prisma-enum `EmployeeType` (zelfde set als uitrusting-doelgroep). */
export const DOSSIER_EMPLOYEE_TYPES = [
  'ONDERHOUD',
  'STORING',
  'SOFTWARE',
  'PROJECT',
  'UNIVERSEEL',
  'KANTOOR',
] as const
export type DossierEmployeeType = (typeof DOSSIER_EMPLOYEE_TYPES)[number]

// ─── Zod: vereisten-beheer (FO-355 §6) ───────────────────────────────────────

const dossierVereisteBasis = {
  label: z.string().trim().min(1, 'Label is verplicht').max(120),
  verplicht: z.boolean().optional().default(true),
  employeeTypes: z.array(z.enum(DOSSIER_EMPLOYEE_TYPES)).optional().default([]),
  zelfAanleverbaar: z.boolean().optional().default(true),
  actief: z.boolean().optional().default(true),
  escalatieDagen: z.number().int().min(7).max(365).optional().default(30),
}

/**
 * Aanmaken van een vereiste: DOCUMENT-sleutels uit `DocumentType`,
 * GEGEVEN-sleutels uit de vaste set (spec 57 §6).
 */
export const CreateDossierVereisteSchema = z.discriminatedUnion('soort', [
  z.object({ soort: z.literal('DOCUMENT'), sleutel: z.enum(DOCUMENT_TYPES), ...dossierVereisteBasis }),
  z.object({ soort: z.literal('GEGEVEN'), sleutel: z.enum(DOSSIER_GEGEVEN_KEYS), ...dossierVereisteBasis }),
])
export type CreateDossierVereisteInput = z.infer<typeof CreateDossierVereisteSchema>

/** Bijwerken: soort/sleutel liggen vast (identiteit van de vereiste). */
export const UpdateDossierVereisteSchema = z.object({
  label: z.string().trim().min(1).max(120).optional(),
  verplicht: z.boolean().optional(),
  employeeTypes: z.array(z.enum(DOSSIER_EMPLOYEE_TYPES)).optional(),
  zelfAanleverbaar: z.boolean().optional(),
  actief: z.boolean().optional(),
  escalatieDagen: z.number().int().min(7).max(365).optional(),
})
export type UpdateDossierVereisteInput = z.infer<typeof UpdateDossierVereisteSchema>

/** Snooze op medewerker-niveau (FO-355 §7.3) of op een verzoek (FO-358 §8.2). */
export const DossierSnoozeSchema = z.object({
  dagen: z.union([z.literal(30), z.literal(60), z.literal(90)]),
  reden: z.string().trim().min(3, 'Reden is verplicht').max(500),
})
export type DossierSnoozeInput = z.infer<typeof DossierSnoozeSchema>

// ─── Zod: informatie-verzoek (FO-358 §7) ─────────────────────────────────────

export const CreateInformatieVerzoekSchema = z
  .object({
    /** Vereiste-items van het volledigheids-blok (soort + sleutel). */
    items: z
      .array(
        z.discriminatedUnion('soort', [
          z.object({ soort: z.literal('DOCUMENT'), sleutel: z.enum(DOCUMENT_TYPES) }),
          z.object({ soort: z.literal('GEGEVEN'), sleutel: z.enum(DOSSIER_GEGEVEN_KEYS) }),
        ]),
      )
      .optional()
      .default([]),
    /** Vrije aanvragen: onderwerp verplicht, max 120 (spec 60 §7). */
    vrijeItems: z
      .array(z.object({ onderwerp: z.string().trim().min(1, 'Onderwerp is verplicht').max(120) }))
      .optional()
      .default([]),
    toelichting: z.string().trim().max(2000).optional(),
    /** Duplicaat-guard: true = bewust opnieuw versturen na de waarschuwing. */
    bevestigDuplicaat: z.boolean().optional().default(false),
  })
  .refine((v) => v.items.length + v.vrijeItems.length > 0, {
    message: 'Kies minimaal één item of voeg een vrije aanvraag toe',
    path: ['items'],
  })
export type CreateInformatieVerzoekInput = z.infer<typeof CreateInformatieVerzoekSchema>

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Eén ontbrekend (of onderweg zijnd) dossier-item. */
export interface DossierOntbrekendItemDTO {
  soort: DossierVereisteSoortValue
  sleutel: string
  label: string
  zelfAanleverbaar: boolean
  /**
   * True = er staat een ingediende aanlevering (document-aanvraag of
   * mutatie-verzoek) open; de melding wordt onderdrukt maar het item telt nog
   * niet als aanwezig (spec 57 §3.2).
   */
  onderweg: boolean
  /** Deeplink-tab in /mijn-gegevens (null = niet zelf aanleverbaar). */
  mijnGegevensTab: DossierMijnGegevensTab | null
}

/** Volledigheid van één dossier (blok, banner, melding delen deze bron). */
export interface DossierVolledigheidDTO {
  totaal: number
  aanwezig: number
  /** Ontbrekend zonder open aanlevering — exact de lijst uit de melding. */
  ontbrekend: DossierOntbrekendItemDTO[]
  /** Ontbrekend mét open aanlevering (melding onderdrukt). */
  onderweg: DossierOntbrekendItemDTO[]
  compleet: boolean
}

/** Blok-respons backoffice (`GET /employees/:id/dossier-volledigheid`). */
export interface DossierVolledigheidBlokDTO extends DossierVolledigheidDTO {
  snooze: {
    tot: string
    reden: string | null
    doorNaam: string | null
  } | null
}

export interface DossierVereisteDTO {
  id: string
  soort: DossierVereisteSoortValue
  sleutel: string
  label: string
  verplicht: boolean
  employeeTypes: DossierEmployeeType[]
  zelfAanleverbaar: boolean
  actief: boolean
  escalatieDagen: number
  createdAt: string
  updatedAt: string
}

/** Impact-telling vóór activering (FO-355 §7.1 — read-only). */
export interface DossierVereisteImpactDTO {
  /** Aantal medewerkers (in dienst + onboarding) dat dit item nu mist. */
  ontbreektBij: number
  /** Totaal aantal medewerkers in de doelgroep-scope. */
  doelgroep: number
}

export interface InformatieVerzoekItemDTO {
  id: string
  soort: DossierVereisteSoortValue | null
  sleutel: string | null
  label: string
  aanwezigOp: string | null
  vervallenOp: string | null
}

export interface InformatieVerzoekDTO {
  id: string
  employeeId: string
  status: InformatieVerzoekStatusValue
  toelichting: string | null
  aangevraagdDoorNaam: string
  aangevraagdOp: string
  mailKanaal: 'WERK' | 'PRIVE' | 'GEEN' | null
  herinneringTeller: number
  laatsteHerinneringOp: string | null
  afgerondOp: string | null
  geannuleerdOp: string | null
  vervallenOp: string | null
  vervallenReden: string | null
  snoozeTot: string | null
  items: InformatieVerzoekItemDTO[]
}

/** Portal-banner (`GET /me/dossier-volledigheid`): eigen items + open verzoeken. */
export interface MijnDossierVolledigheidDTO {
  /** Alleen zelf-aanleverbare ontbrekende items (zelfde lijst als de melding). */
  ontbrekend: DossierOntbrekendItemDTO[]
  /** Items met een ingediende aanlevering die op beoordeling wacht. */
  onderweg: DossierOntbrekendItemDTO[]
  /** Open informatie-verzoeken van HR (FO-358) met toelichting. */
  openVerzoeken: Array<{
    id: string
    aangevraagdOp: string
    toelichting: string | null
    items: Array<{
      label: string
      mijnGegevensTab: DossierMijnGegevensTab | null
    }>
  }>
}
